import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    differenceInCalendarWeeks,
    format,
    getDate,
    getDay,
    getDaysInMonth,
    getMonth,
    getYear,
    parse,
    setDate,
} from 'date-fns';

const DATE_FMT = 'yyyy-MM-dd';

export type FrequencyUnit = 'daily' | 'weekly' | 'monthly' | 'annual';

export type InferredFrequency = { frequency: string; interval: number };

/**
 * Campos que definem o padrão de repetição de uma regra. As funções de "ocorrência"
 * (nextOccurrence, occurrenceIndexOf, ...) operam sobre este objeto e despacham para
 * os modos avançados da Fase 2 (semanal multi-dia e mensal por posição) quando presentes.
 */
export type RecurrenceRuleFields = {
    frequency: string;
    interval: number;
    startDate: string;
    weekdays?: string | null;     // semanal multi-dia (BYDAY), ex.: "2,4" (getDay: 0=dom)
    monthlyMode?: string | null;  // 'nth_weekday' para "1º sábado"; senão dia-do-mês
};

function parseDate(value: string): Date {
    return parse(value, DATE_FMT, new Date());
}

/**
 * Datas puladas de uma regra (Exceção por exclusão). Armazenadas como JSON `["yyyy-MM-dd"]`
 * na coluna `skipped_dates`. Devolve um Set para lookup O(1) nas engines de projeção/geração.
 * Tolerante a null/JSON inválido (degrada para vazio). Ver docs/adr/0014.
 */
export function parseSkippedDates(skippedDates?: string | null): Set<string> {
    if (!skippedDates) return new Set();
    try {
        const parsed = JSON.parse(skippedDates);
        if (Array.isArray(parsed)) {
            return new Set(parsed.filter((value): value is string => typeof value === 'string'));
        }
    } catch {
        // JSON corrompido: trata como "nenhuma data pulada".
    }
    return new Set();
}

/**
 * Dia-do-mês de uma data ISO (`yyyy-MM-dd`), usado como âncora da recorrência
 * mensal/anual para evitar drift em meses curtos (ver docs/adr/0002).
 */
export function anchorDayOf(startDate: string): number {
    return Number(startDate.slice(8, 10));
}

function parseWeekdays(weekdays?: string | null): number[] {
    if (!weekdays) return [];
    return weekdays
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
        .sort((left, right) => left - right);
}

export function isWeeklyMultiDay(rule: Pick<RecurrenceRuleFields, 'frequency' | 'weekdays'>): boolean {
    return rule.frequency === 'weekly' && parseWeekdays(rule.weekdays).length > 0;
}

export function isNthWeekday(rule: Pick<RecurrenceRuleFields, 'frequency' | 'monthlyMode'>): boolean {
    return rule.frequency === 'monthly' && rule.monthlyMode === 'nth_weekday';
}

// ─── Núcleo posicional (Fase 1): unidade base + interval + ancoragem ──────────

function resolveStep(frequency: string, interval: number): { unit: FrequencyUnit; step: number } {
    switch (frequency) {
        case 'daily': return { unit: 'daily', step: interval };
        case 'weekly': return { unit: 'weekly', step: interval };
        case 'monthly': return { unit: 'monthly', step: interval };
        case 'annual': return { unit: 'annual', step: interval };
        case 'biweekly': return { unit: 'weekly', step: 2 };
        case 'bimonthly': return { unit: 'monthly', step: 2 };
        case 'quarterly': return { unit: 'monthly', step: 3 };
        case 'semiannual': return { unit: 'monthly', step: 6 };
        default: return { unit: 'monthly', step: interval };
    }
}

function applyStep(date: Date, unit: FrequencyUnit, step: number, anchorDay: number): Date {
    switch (unit) {
        case 'daily':
            return addDays(date, step);
        case 'weekly':
            return addWeeks(date, step);
        case 'monthly': {
            const moved = addMonths(date, step);
            return setDate(moved, Math.min(anchorDay, getDaysInMonth(moved)));
        }
        case 'annual': {
            const moved = addYears(date, step);
            return setDate(moved, Math.min(anchorDay, getDaysInMonth(moved)));
        }
    }
}

export function getNextDate(
    currentDate: string,
    frequency: string,
    interval = 1,
    anchorDay?: number,
): string {
    const { unit, step } = resolveStep(frequency, interval);
    const anchor = anchorDay ?? anchorDayOf(currentDate);
    return format(applyStep(parseDate(currentDate), unit, step, anchor), DATE_FMT);
}

export function getPreviousDate(
    currentDate: string,
    frequency: string,
    interval = 1,
    anchorDay?: number,
): string {
    const { unit, step } = resolveStep(frequency, interval);
    const anchor = anchorDay ?? anchorDayOf(currentDate);
    return format(applyStep(parseDate(currentDate), unit, -step, anchor), DATE_FMT);
}

export function getOccurrenceIndex(
    startDate: string,
    targetDate: string,
    frequency: string,
    interval = 1,
): number | null {
    if (startDate === targetDate) return 0;
    if (targetDate < startDate) return null;

    const anchor = anchorDayOf(startDate);
    let current = startDate;
    let index = 0;
    let guard = 2000;

    while (current < targetDate && guard > 0) {
        guard -= 1;
        current = getNextDate(current, frequency, interval, anchor);
        index += 1;
        if (current === targetDate) return index;
    }

    return null;
}

export function getOccurrenceDateAtIndex(
    startDate: string,
    frequency: string,
    occurrenceIndex: number,
    interval = 1,
): string {
    const anchor = anchorDayOf(startDate);
    let current = startDate;
    let remaining = Math.max(0, occurrenceIndex);
    while (remaining > 0) {
        current = getNextDate(current, frequency, interval, anchor);
        remaining -= 1;
    }
    return current;
}

export function getFirstOccurrenceOnOrAfter(
    startDate: string,
    frequency: string,
    floorDate: string,
    interval = 1,
    anchorDay?: number,
): string {
    if (startDate >= floorDate) return startDate;

    const anchor = anchorDay ?? anchorDayOf(startDate);
    let current = startDate;
    let guard = 2000;
    while (current < floorDate && guard > 0) {
        guard -= 1;
        const next = getNextDate(current, frequency, interval, anchor);
        if (next <= current) break;
        current = next;
    }
    return current;
}

export function rewindOccurrenceDate(
    date: string,
    frequency: string,
    occurrences: number,
    interval = 1,
    anchorDay?: number,
): string {
    const anchor = anchorDay ?? anchorDayOf(date);
    let current = date;
    let remaining = Math.max(0, occurrences);
    while (remaining > 0) {
        current = getPreviousDate(current, frequency, interval, anchor);
        remaining -= 1;
    }
    return current;
}

// ─── Fase 2: semanal multi-dia (BYDAY) ────────────────────────────────────────

function isActiveWeek(date: Date, start: Date, interval: number): boolean {
    const weekIndex = differenceInCalendarWeeks(date, start, { weekStartsOn: 0 });
    return weekIndex >= 0 && weekIndex % Math.max(1, interval) === 0;
}

function isWeeklyMultiDayOccurrence(date: Date, rule: RecurrenceRuleFields, weekdays: number[]): boolean {
    const start = parseDate(rule.startDate);
    if (date < start) return false;
    return weekdays.includes(getDay(date)) && isActiveWeek(date, start, rule.interval);
}

function nextWeeklyMultiDay(current: string, rule: RecurrenceRuleFields, weekdays: number[]): string {
    const start = parseDate(rule.startDate);
    let cursor = addDays(parseDate(current), 1);
    let guard = 7 * Math.max(1, rule.interval) + 8;
    while (guard > 0) {
        guard -= 1;
        if (weekdays.includes(getDay(cursor)) && isActiveWeek(cursor, start, rule.interval)) {
            return format(cursor, DATE_FMT);
        }
        cursor = addDays(cursor, 1);
    }
    return current;
}

function previousWeeklyMultiDay(current: string, rule: RecurrenceRuleFields, weekdays: number[]): string {
    const start = parseDate(rule.startDate);
    let cursor = addDays(parseDate(current), -1);
    let guard = 7 * Math.max(1, rule.interval) + 8;
    while (guard > 0 && cursor >= start) {
        guard -= 1;
        if (weekdays.includes(getDay(cursor)) && isActiveWeek(cursor, start, rule.interval)) {
            return format(cursor, DATE_FMT);
        }
        cursor = addDays(cursor, -1);
    }
    return current;
}

// ─── Fase 2: mensal por posição ("1º sábado") ─────────────────────────────────

function nthWeekdayOfMonth(year: number, month: number, weekday: number, position: number, useLast: boolean): Date {
    const first = new Date(year, month, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    const daysInMonth = getDaysInMonth(first);
    let day = 1 + offset + (position - 1) * 7;
    if (useLast || day > daysInMonth) {
        day = 1 + offset + Math.floor((daysInMonth - 1 - offset) / 7) * 7;
    }
    return new Date(year, month, day);
}

function nthWeekdayParams(rule: RecurrenceRuleFields): { weekday: number; position: number; useLast: boolean } {
    const start = parseDate(rule.startDate);
    const dayOfMonth = getDate(start);
    const position = Math.floor((dayOfMonth - 1) / 7) + 1;
    const useLast = dayOfMonth + 7 > getDaysInMonth(start);
    return { weekday: getDay(start), position, useLast };
}

function stepNthWeekday(current: string, rule: RecurrenceRuleFields, direction: 1 | -1): string {
    const { weekday, position, useLast } = nthWeekdayParams(rule);
    const cursor = parseDate(current);
    const target = addMonths(new Date(getYear(cursor), getMonth(cursor), 1), direction * Math.max(1, rule.interval));
    const result = nthWeekdayOfMonth(getYear(target), getMonth(target), weekday, position, useLast);
    return format(result, DATE_FMT);
}

// ─── API objeto-regra (despacha simples vs avançado) ──────────────────────────

export function nextOccurrence(current: string, rule: RecurrenceRuleFields): string {
    const weekdays = parseWeekdays(rule.weekdays);
    if (rule.frequency === 'weekly' && weekdays.length > 0) {
        return nextWeeklyMultiDay(current, rule, weekdays);
    }
    if (isNthWeekday(rule)) {
        return stepNthWeekday(current, rule, 1);
    }
    return getNextDate(current, rule.frequency, rule.interval, anchorDayOf(rule.startDate));
}

export function previousOccurrence(current: string, rule: RecurrenceRuleFields): string {
    const weekdays = parseWeekdays(rule.weekdays);
    if (rule.frequency === 'weekly' && weekdays.length > 0) {
        return previousWeeklyMultiDay(current, rule, weekdays);
    }
    if (isNthWeekday(rule)) {
        return stepNthWeekday(current, rule, -1);
    }
    return getPreviousDate(current, rule.frequency, rule.interval, anchorDayOf(rule.startDate));
}

/** Primeira ocorrência da regra (índice 0): a startDate, ou a 1ª data válida >= startDate no multi-dia. */
export function firstOccurrence(rule: RecurrenceRuleFields): string {
    const weekdays = parseWeekdays(rule.weekdays);
    if (rule.frequency === 'weekly' && weekdays.length > 0) {
        if (isWeeklyMultiDayOccurrence(parseDate(rule.startDate), rule, weekdays)) {
            return rule.startDate;
        }
        return nextWeeklyMultiDay(rule.startDate, rule, weekdays);
    }
    return rule.startDate;
}

export function occurrenceDateAt(occurrenceIndex: number, rule: RecurrenceRuleFields): string {
    if (!isWeeklyMultiDay(rule) && !isNthWeekday(rule)) {
        return getOccurrenceDateAtIndex(rule.startDate, rule.frequency, occurrenceIndex, rule.interval);
    }
    let current = firstOccurrence(rule);
    let remaining = Math.max(0, occurrenceIndex);
    while (remaining > 0) {
        current = nextOccurrence(current, rule);
        remaining -= 1;
    }
    return current;
}

export function occurrenceIndexOf(targetDate: string, rule: RecurrenceRuleFields): number | null {
    if (!isWeeklyMultiDay(rule) && !isNthWeekday(rule)) {
        return getOccurrenceIndex(rule.startDate, targetDate, rule.frequency, rule.interval);
    }
    const base = firstOccurrence(rule);
    if (targetDate < base) return null;
    if (targetDate === base) return 0;
    let current = base;
    let index = 0;
    let guard = 4000;
    while (current < targetDate && guard > 0) {
        guard -= 1;
        current = nextOccurrence(current, rule);
        index += 1;
        if (current === targetDate) return index;
    }
    return null;
}

export function firstOccurrenceFrom(floorDate: string, rule: RecurrenceRuleFields, seed?: string): string {
    if (!isWeeklyMultiDay(rule) && !isNthWeekday(rule)) {
        return getFirstOccurrenceOnOrAfter(
            seed ?? rule.startDate,
            rule.frequency,
            floorDate,
            rule.interval,
            anchorDayOf(rule.startDate),
        );
    }
    let current = seed ?? firstOccurrence(rule);
    let guard = 4000;
    while (current < floorDate && guard > 0) {
        guard -= 1;
        const next = nextOccurrence(current, rule);
        if (next <= current) break;
        current = next;
    }
    return current;
}

export function rewindFrom(date: string, occurrences: number, rule: RecurrenceRuleFields): string {
    if (!isWeeklyMultiDay(rule) && !isNthWeekday(rule)) {
        return rewindOccurrenceDate(date, rule.frequency, occurrences, rule.interval, anchorDayOf(date));
    }
    let current = date;
    let remaining = Math.max(0, occurrences);
    while (remaining > 0) {
        const previous = previousOccurrence(current, rule);
        if (previous >= current) break;
        current = previous;
        remaining -= 1;
    }
    return current;
}

/**
 * A partir de `fromSlot`, avança enquanto o slot estiver em `skipped` (respeitando endDate),
 * retornando o primeiro slot NÃO-pulado. Se `fromSlot` não está pulado, devolve ele mesmo.
 * Garante que o ponteiro "próxima cobrança" nunca aponte para uma ocorrência pulada — tanto ao
 * pular o slot da frente (skipOccurrence) quanto ao pagar/confirmar (payOccurrence/settleOccurrence)
 * ou quebrar a série (split "esta e futuras").
 */
export function advancePastSkipped(
    rule: RecurrenceRuleFields & { endDate: string | null },
    fromSlot: string,
    skipped: Set<string>,
): string {
    let current = fromSlot;
    let guard = 1000;
    while (skipped.has(current) && guard > 0) {
        guard -= 1;
        if (rule.endDate && current > rule.endDate) break;
        const next = nextOccurrence(current, rule);
        if (next <= current) break;
        current = next;
    }
    return current;
}

/**
 * Remapeia as datas puladas (Exceção por exclusão — ver docs/adr/0014) quando a série é
 * reancorada: rebase de data no escopo "todas" ou split "esta e futuras". O pulo segue a
 * POSIÇÃO do slot na série, não a data absoluta — a mesma regra que o split usa para
 * nextOccurrence/endDate. Sem mudança de data o remap reproduz as mesmas datas absolutas.
 * Pulos anteriores ao índice-âncora (ficam com a regra antiga no split) ou que não alinham
 * a nenhum slot da regra antiga morrem no remap.
 */
export function rebaseSkippedDates(
    oldRule: RecurrenceRuleFields,
    skippedDates: Set<string>,
    newRule: RecurrenceRuleFields,
    anchorIndex = 0,
): string[] {
    const result: string[] = [];
    for (const skippedDate of skippedDates) {
        const index = occurrenceIndexOf(skippedDate, oldRule);
        if (index === null || index < anchorIndex) continue;
        result.push(occurrenceDateAt(index - anchorIndex, newRule));
    }
    return result.sort();
}

/**
 * As próximas `count` ocorrências da regra a partir de `fromDate` (ou da 1ª ocorrência),
 * respeitando endDate. Usado no preview do editor "Personalizar" (read-only).
 */
export function upcomingOccurrences(
    rule: RecurrenceRuleFields,
    count: number,
    fromDate?: string,
    endDate?: string | null,
): string[] {
    const result: string[] = [];
    let current = fromDate ? firstOccurrenceFrom(fromDate, rule) : firstOccurrence(rule);
    let guard = count * (Math.max(1, rule.interval) + 1) * 8 + 60;
    while (result.length < count && guard > 0) {
        guard -= 1;
        if (endDate && current > endDate) break;
        result.push(current);
        const next = nextOccurrence(current, rule);
        if (next <= current) break;
        current = next;
    }
    return result;
}

/**
 * Data efetiva de término de uma regra que termina "após N ocorrências".
 * A N-ésima ocorrência é o índice N-1. Ver docs/features/RECURRENCE.md.
 */
export function deriveEndDateFromCount(rule: RecurrenceRuleFields, count: number): string {
    const safeCount = Math.max(1, Math.floor(count));
    return occurrenceDateAt(safeCount - 1, rule);
}

// ─── Detecção de frequência a partir de datas (import) ─────────────────────────

export function inferFrequencyFromDates(dates: string[]): InferredFrequency | null {
    const uniqueDates = [...new Set(dates)].sort();
    if (uniqueDates.length < 2) return null;

    const candidates: InferredFrequency[] = [
        { frequency: 'daily', interval: 1 },
        { frequency: 'weekly', interval: 1 },
        { frequency: 'weekly', interval: 2 },
        { frequency: 'monthly', interval: 1 },
        { frequency: 'monthly', interval: 2 },
        { frequency: 'monthly', interval: 3 },
        { frequency: 'monthly', interval: 6 },
        { frequency: 'annual', interval: 1 },
    ];

    const anchor = anchorDayOf(uniqueDates[0]);

    for (const candidate of candidates) {
        let matches = true;
        for (let index = 1; index < uniqueDates.length; index += 1) {
            const expected = getNextDate(uniqueDates[index - 1], candidate.frequency, candidate.interval, anchor);
            if (expected !== uniqueDates[index]) {
                matches = false;
                break;
            }
        }
        if (matches) return candidate;
    }

    return null;
}
